// Native LOCAL notifications for the Android shell — the OS-side half of the rest timer.
//
// This is not push: nothing comes from the server. It hands a deadline to Android's AlarmManager
// (via @capacitor/local-notifications, reached at runtime through window.Capacitor.Plugins like
// the HealthConnect and PushNotifications plugins) so the alert fires on time even though a
// backgrounded WebView has its JS timers throttled to roughly once a minute — which is exactly
// why "rest over" never buzzed once Kashtu was off screen.
//
// Absent on plain web and on APKs built before the plugin shipped, so every call is a safe no-op
// there; the PWA keeps using the service-worker notification in RestTimer.js.

const REST_ID = 4301; // stable id — re-scheduling replaces the pending alarm instead of stacking

function plugin() {
  return (typeof window !== "undefined" && window.Capacitor?.Plugins?.LocalNotifications) || null;
}

function sharedPlugin() {
  return (typeof window !== "undefined" && window.Capacitor?.Plugins?.SharedRest) || null;
}

/** True only when native local notifications are actually callable. */
export function available() {
  return !!(sharedPlugin() || plugin());
}

let permission = null; // "granted" | "denied", cached for the session

async function ensurePermission(p) {
  if (permission) return permission === "granted";
  let res = await p.checkPermissions().catch(() => null);
  // The shell already asks for POST_NOTIFICATIONS on launch, so this is normally a no-op read.
  if (res?.display !== "granted") res = await p.requestPermissions().catch(() => null);
  permission = res?.display === "granted" ? "granted" : "denied";
  return permission === "granted";
}

/** Schedule the "rest complete" alert for `at` (a Date). Replaces any pending one. */
export async function scheduleRestOver(at) {
  const shared = sharedPlugin();
  const p = plugin();
  if (!shared && !p) return;
  const when = at instanceof Date ? at : new Date(at);
  if (!(when.getTime() > Date.now() + 500)) return; // already due — the in-app tick handles it
  try {
    // Newer APKs use the same AlarmManager PendingIntent as background FCM rest updates.
    // Whichever path arrives last merely reschedules that one alarm, so cross-device sync
    // cannot double-ping.
    if (shared) {
      await shared.schedule({ at: when.getTime() });
      return;
    }
    if (!(await ensurePermission(p))) return;
    await p.schedule({
      notifications: [{
        id: REST_ID,
        title: "Rest complete 💪",
        body: "Time for your next set.",
        // allowWhileIdle → setExactAndAllowWhileIdle, so Doze can't hold the alert back.
        schedule: { at: when, allowWhileIdle: true },
      }],
    });
  } catch { /* best-effort: the in-app countdown is still running */ }
}

/**
 * A rest change made from a notification button while no page was running — "+30 s" or "Skip"
 * tapped on the lock screen. That change is already live on this phone's alarm, but the SERVER
 * holds the authoritative deadline and knows nothing about it, so the next poll would read the
 * old value back and quietly undo the tap. The page pushes it up instead, which is far cheaper
 * than teaching a BroadcastReceiver to authenticate and POST.
 *
 * Read-and-clear on the native side: calling twice returns nothing the second time.
 * Resolves to `{ endsAt }` (ISO string, or null when the rest was skipped), else null.
 */
export async function takePendingRestChange() {
  const shared = sharedPlugin();
  if (!shared?.takePendingChange) return null; // older APKs simply have nothing to report
  try {
    const res = await shared.takePendingChange();
    if (!res?.changed) return null;
    return { endsAt: res.endsAt ? new Date(Number(res.endsAt)).toISOString() : null };
  } catch {
    return null;
  }
}

/** Drop the pending rest alert (skipped, finished in-app, or we're back in the foreground). */
export async function cancelRestOver() {
  const shared = sharedPlugin();
  const p = plugin();
  if (!shared && !p) return;
  try {
    if (shared) await shared.cancel();
    else await p.cancel({ notifications: [{ id: REST_ID }] });
  } catch { /* ignore */ }
}
